
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  provide,
  watch,
  Ref,
  defineAsyncComponent,
} from 'vue';
import { ObjectiveFilter, HISTORY, Messages } from '../../types';
import Authentication from 'src/boot/services/Authentication';
import { useQuestions } from '../../pinia/useQuestions';
import { storeToRefs } from 'pinia';
import axios from 'axios';
import NotifyHelper from 'src/boot/NotifyHelper';
import HelpBtn from '../basic/HelpBtn.vue';
import { debounce } from 'lodash';

export default defineComponent({
  name: 'DiscursiveFilter',
  components: {
    FilterBanner: defineAsyncComponent(
      () => import('../basic/FilterBanner.vue')
    ),
    GenericSelect: defineAsyncComponent(
      () => import('../basic/GenericSelect.vue')
    ),
    ObjectiveShower: defineAsyncComponent(
      () => import('../objectives/ObjectiveShower.vue')
    ),
    HelpBtn,
  },
  props: {
    subject: {
      type: String,
      required: false,
    },
    topic: {
      type: String,
      required: false,
    },
    isReviewed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const user = Authentication.loggedUser();
    const hideJury = user?.area.modules.includes('hide-jury');
    const useQuestion = useQuestions();
    const {
      totalCountWrong,
      countResult,
      countWrongResult,
      getMinYears,
      getMaxYears,
      isLoadingYears,
      jury,
      questions,
      currentQuestion,
      subjects,
    } = storeToRefs(useQuestion);
    const loading = ref(true);
    const questionIndex = ref(0);
    const maxResults = ref(50);
    const pagination = ref(1);
    const page = ref(0);
    const isReview = ref(props.isReviewed);
    const expansionTitle = 'Total: ';
    const filterBtn = isReview.value ? 'Revisar' : 'Filtrar';
    const totalPages = ref(0);
    const fontSize = ref(14);
    const min = getMinYears;
    const max = getMaxYears;
    const range = ref({ min: getMinYears, max: getMaxYears });
    const isFilterExpanded = ref(true);
    const subjectSelected = ref<string[]>([]);
    const topics = ref<string[]>();
    const topicSelected = ref<string[]>([]);
    const difficultySelected = ref<string[]>([]);
    const topicAux: Ref<string[]> = ref([]);
    const history = HISTORY;
    const jurySelected = ref<string[]>([]);
    const isStarting = ref(false);
    const historyFilter = ref(
      isReview.value ? HISTORY[3].value : HISTORY[0].value
    );
    const historyOptions = HISTORY;
    const difficultyOptions = ['Fácil', 'Normal', 'Difícil'];
    const init = ref(false);
    const pageCounter = computed(
      () => `${page.value + 1} de ${totalPages.value}`
    );
    const isFirstPage = computed(() => page.value === 0);
    const isLastPage = computed(() => page.value === totalPages.value - 1);

    let subjectsWithTopics =
      user?.contest.subjects.map((el) => {
        return {
          subject: el.subject,
          topics: el.topics
            .filter((el) => el.has_questions === true)
            .map((el) => el.name),
        };
      }) || [];

    const selectedJuryIsDefault = computed(
      () => jurySelected.value.length === 0
    );
    const selectedSubjectIsDefault = computed(
      () => subjectSelected.value.length === 0
    );
    const selectedTopicIsDefault = computed(
      () => topicSelected.value.length === 0
    );
    const selectedPeriodIsDefault = computed(
      () => range.value.min === min.value && range.value.max === max.value
    );
    const fieldsIsDefault = computed(
      () =>
        selectedJuryIsDefault.value &&
        selectedPeriodIsDefault.value &&
        selectedSubjectIsDefault.value &&
        selectedTopicIsDefault.value
    );

    const notFoundObjectives = computed(() => Number(countResult.value) === 0);
    const notFoundReview = computed(() => Number(countWrongResult.value) === 0);
    const timeline = computed(() => `${range.value.min} - ${range.value.max}`);
    const filter = computed(() => {
      const filter: ObjectiveFilter = {};
      if (subjectSelected.value.length)
        filter.subject = { $in: subjectSelected.value };
      if (topicSelected.value.length)
        filter.topic = { $in: topicSelected.value };
      if (range.value)
        filter.info = {
          year: {
            $lte: range.value.max,
            $gte: range.value.min,
          },
        };
      if (jurySelected.value.length)
        filter.info = {
          ...filter.info,
          jury: { $in: jurySelected.value },
        };
      if (
        difficultySelected.value.length !== 0 &&
        difficultySelected.value.length !== 3
      ) {
        if (difficultySelected.value.length === 1) {
          if (difficultySelected.value.includes('Fácil')) {
            filter.b = {
              $lte: 0,
            };
          } else if (difficultySelected.value.includes('Normal')) {
            filter.b = {
              $gt: 0,
              $lte: 0.5,
            };
          } else {
            filter.b = {
              $gt: 0.5,
            };
          }
        } else if (difficultySelected.value.length === 2) {
          if (
            difficultySelected.value.includes('Fácil') &&
            difficultySelected.value.includes('Normal')
          ) {
            filter.b = {
              $lte: 0.5,
            };
          }
          if (
            difficultySelected.value.includes('Fácil') &&
            difficultySelected.value.includes('Difícil')
          ) {
            filter.b = {
              $lte: 0,
              $gt: 0.5,
            };
          }
          if (
            difficultySelected.value.includes('Normal') &&
            difficultySelected.value.includes('Difícil')
          ) {
            filter.b = {
              $gt: 0,
            };
          }
        }
      }
      return filter;
    });

    const getSubjects = () => {
      return subjectsWithTopics
        ?.filter((el: { topics: string[] }) => {
          return el.topics.length > 0;
        })
        .map((el: { subject: string }) => {
          return el.subject;
        });
    };

    onMounted(async () => {
      loading.value = true;
      const routeSubject = props.subject;
      const routeTopic = props.topic;
      subjects.value = getSubjects();
      if (routeSubject && subjects.value?.includes(routeSubject)) {
        fetchQuestionsFromUrlParams(routeSubject, routeTopic);
      } else {
        await fetchTotalQuestions();
      }
      loading.value = false;
    });

    const fetchQuestionsFromUrlParams = (
      routeSubject: string,
      routeTopic: string | undefined
    ) => {
      subjectSelected.value.push(routeSubject);
      hasTopics();
      if (routeTopic && topics.value?.includes(routeTopic)) {
        topicSelected.value.push(routeTopic);
      }
    };
    const fetchWrongQuestions = async () => {
      loading.value = true;
      totalCountWrong.value = await useQuestion.getTotalQuestions(
        Number(historyFilter.value)
      );
      countWrongResult.value = totalCountWrong.value;
      loading.value = false;
    };
    const fetchAllQuestions = async () => {
      loading.value = true;
      countResult.value = await useQuestion.getTotalQuestions(
        Number(historyFilter.value)
      );
      loading.value = false;
    };
    const fetchTotalQuestions = async () => {
      try {
        if (isReview.value) {
          if (totalCountWrong.value === 0) await fetchWrongQuestions();
          else countWrongResult.value = totalCountWrong.value;
          return;
        }
        if (!countResult.value) await fetchAllQuestions();
      } catch (error) {
        console.error('There was an error fetching total questions', error);
      }
    };

    const startResolution = async () => {
      isStarting.value = true;
      page.value = 0;
      pagination.value = 1;
      await fetchObjectives();
      if (questions.value.length) {
        init.value = true;
        questionIndex.value = 0;
        isFilterExpanded.value = false;
        if (isReview.value) totalPages.value = countWrongResult.value;
        else totalPages.value = countResult.value;
        currentQuestion.value = questions.value[0];
      }
      isStarting.value = false;
    };

    const removeItem = async (arr: string[], index: number) => {
      arr.splice(index, 1);
      hasTopics();
      await countResults();
    };

    const doNotHideFilter = () => {
      if (!init.value) isFilterExpanded.value = true;
    };

    const setDefaultJury = async (arr: string[], index: number) => {
      arr.splice(index, 1);
      hasTopics();
      await countResults();
    };

    const setDefaultRange = () => {
      range.value = {
        min: min.value,
        max: max.value,
      };
    };

    const countResults = async () => {
      try {
        loading.value = true;
        const response = await useQuestion.countQuestions(
          filter.value,
          Number(historyFilter.value)
        );
        if (isReview.value) countWrongResult.value = response;
        else countResult.value = response;
      } catch (error) {
        if (axios.isCancel(error)) {
          console.warn('Request canceled', error);
          return;
        }
        if (isReview.value) countWrongResult.value = 0;
        else countResult.value = 0;
      }
      loading.value = false;
    };

    const debouncedCountResults = debounce(countResults, 1000);

    const fetchObjectives = async () => {
      try {
        let config = {
          where: {},
          page: pagination.value,
          max: maxResults.value,
          history: Number(historyFilter.value),
        };
        config.where = filter.value;
        await useQuestion.fetchObjectives(config);
      } catch (error) {
        console.error(error);
        NotifyHelper.notifyError(Messages.FETCH_QUESTIONS_ERROR);
      }
    };

    const populateTopic = (currentSubjects: string[]) => {
      topicAux.value = [];
      subjectsWithTopics
        ?.filter((subjectsWithTopic) =>
          currentSubjects.includes(subjectsWithTopic.subject)
        )
        .map((subject) => topicAux.value.push(...subject.topics));
      topics.value = [];
      topics.value = topicAux.value;
      topicSelected.value = topicSelected.value.filter((topic) =>
        topicAux.value.includes(topic)
      );
    };

    const hasTopics = () => {
      if (subjectSelected.value.length > 0) {
        populateTopic(subjectSelected.value);
      } else {
        topics.value = [];
        topicSelected.value = [];
      }
    };

    const refresh = async () => {
      if (!isReview.value) await fetchAllQuestions();
      else await fetchWrongQuestions();
    };
    watch(subjectSelected, () => hasTopics());
    watch(topicSelected, async () => {
      loading.value = true;
      await debouncedCountResults();
    });
    watch(difficultySelected, async () => {
      loading.value = true;
      await debouncedCountResults();
    });
    watch(range, async () => {
      loading.value = true;
      await debouncedCountResults();
    });
    watch(jurySelected, async () => {
      loading.value = true;
      await debouncedCountResults();
    });
    watch(historyFilter, async () => {
      loading.value = true;
      await debouncedCountResults();
    });

    provide('pagination', page);
    provide('currentQuestion', currentQuestion);
    provide('totalPages', totalPages);
    provide('fontSize', fontSize);
    provide('isLastPage', isLastPage);
    provide('fetchObjectives', fetchObjectives);
    provide('questionIndex', questionIndex);
    provide('fetchObjectivesPage', pagination);
    provide('maxResults', maxResults);
    provide('isStarting', isStarting);

    return {
      min,
      max,
      init,
      page,
      jury,
      range,
      topics,
      loading,
      history,
      isReview,
      hideJury,
      subjects,
      timeline,
      filterBtn,
      isLastPage,
      isStarting,
      isFirstPage,
      useQuestion,
      countResult,
      pageCounter,
      jurySelected,
      historyFilter,
      topicSelected,
      expansionTitle,
      historyOptions,
      isLoadingYears,
      notFoundReview,
      fieldsIsDefault,
      subjectSelected,
      countWrongResult,
      isFilterExpanded,
      difficultyOptions,
      difficultySelected,
      notFoundObjectives,
      selectedJuryIsDefault,
      selectedTopicIsDefault,
      selectedPeriodIsDefault,
      selectedSubjectIsDefault,
      setDefaultRange,
      startResolution,
      doNotHideFilter,
      setDefaultJury,
      removeItem,
      refresh,
    };
  },
});
